import type {
  ActionOutputData,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { translateAction } from "../../../../../locales/i18n";
import { useRootStore } from "../../../../../stores/hooks";
import { getErrorMessage } from "../../../ActivityGrid/ActivityGrid.helpers";
import { ActionError } from "../ActionError/ActionError";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import { GeneralPdfGroupWrapper } from "../GeneratePdfGroupWrapper/GeneratePdfGroupWrapper";
import { useRecordJobData } from "../../hooks/useRecordJobData";
import { isOldTypejob } from "../../utils";
import { getActionRecordCount } from "../../../shared/status-walk";

interface GeneratePdfProps {
  action: ActionOutputData;
  /** Top-level group the action came from; record-job paths are keyed by it. */
  groupInstanceId?: number;
  onDetailsClick?: () => void;
}

export const GeneratePdfGroup = observer<GeneratePdfProps>(
  ({ action, groupInstanceId = 0, onDetailsClick }) => {
    const { integrations, jobDetails } = useRootStore();
    const selectedIntegration = integrations.list.find(
      (i) => i.integrationId === action.actionReference?.integrationId,
    );
    const selectedAction = selectedIntegration?.actions.find(
      (a) => a.actionId === action.actionReference?.actionId,
    );

    const [recordJobsFiltered, setRecordJobsFiltered] = useState<
      RecordJobData[]
    >([]);

    const [recordJobData, setRecordJobData] = useState<
      Record<string, RecordJobData>
    >({});

    useEffect(() => {
      const recordJobDatas = jobDetails.recordJobData;
      setRecordJobsFiltered(
        recordJobDatas.filter(
          (recordJobData) =>
            recordJobData.actionReference?.instanceId ===
            action.actionReference?.instanceId,
        ),
      );
    }, [jobDetails.recordJobData, action.actionReference?.instanceId]);

    const { fetchRecordJobData } = useRecordJobData({
      recordJobData,
      setRecordJobData,
    });

    const recordCount = getActionRecordCount(action, jobDetails.job);

    const oldTypeJob = useMemo(
      () => isOldTypejob(jobDetails),
      [jobDetails.job],
    );

    const status = action.status.oneofKind === "ok" ? "is_success" : "is_error";

    const errorMessage = getErrorMessage(action, jobDetails.job);

    const onDetailsClickWithFetch = async (
      recordJodId: string,
    ): Promise<void> => {
      await fetchRecordJobData(recordJodId);
    };

    return (
      <ActivityDetailsGroup
        label={
          selectedAction !== undefined ? translateAction(selectedAction) : ""
        }
        status={status}
      >
        {status === "is_error" && <ActionError errorMessage={errorMessage} />}

        {!oldTypeJob &&
          recordCount > 0 &&
          Array.from({ length: recordCount }).map((_, idx) => {
            const recordJodId = `jobs/${jobDetails.job.jobId}/group-${groupInstanceId}/action-${
              action.actionReference?.instanceId ?? ""
            }/record-${idx}`;

            return (
              <GeneralPdfGroupWrapper
                key={recordJodId}
                index={idx}
                recordJodId={recordJodId}
                recordJob={recordJobData[recordJodId]}
                onDetailsClickWithFetch={onDetailsClickWithFetch}
              />
            );
          })}
        {oldTypeJob &&
          recordJobsFiltered.map((recordJob, index) => {
            const recordJodId = `jobs/${jobDetails.job.jobId}/group-0/action-${
              action.actionReference?.instanceId ?? ""
            }/record-${index}`;
            return (
              <GeneralPdfGroupWrapper
                key={recordJodId}
                index={index}
                recordJob={recordJob}
                onDetailsClick={onDetailsClick}
              />
            );
          })}
      </ActivityDetailsGroup>
    );
  },
);
